import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Menu, X, MessageCircle, Shield, Upload, LogIn, LogOut, User, Package, CircleDollarSign, BookOpen, Mail, Phone, Users, Tag, FolderOpen } from 'lucide-react';
import { CONTACT_INFO, getWhatsAppLink } from './lib/utils';
import CollectiblesList from './components/CollectiblesList';
import CollectibleDetail from './components/CollectibleDetail';
import EditCollectible from './components/EditCollectible';
import UploadCollectible from './components/UploadCollectible';
import BulkUpload from './components/BulkUpload';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import AdminBlogPosts from './components/AdminBlogPosts';
import BlogPostEditor from './components/BlogPostEditor';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsConditions from './components/TermsConditions';
import ReturnPolicy from './components/ReturnPolicy';
import ShippingPolicy from './components/ShippingPolicy';
import AdminOrders from './components/AdminOrders';
import AdminCollectibles from './components/AdminCollectibles';
import AdminUsers from './components/AdminUsers';
import AdminSellerSubmissions from './components/AdminSellerSubmissions';
import AdminPromoCode from './components/AdminPromoCode';
import AdminCategories from './components/AdminCategories';
import SellCollectible from './components/SellCollectible';
import { AuthModal } from './components/AuthModal';
import { OrganizationJsonLd, WebSiteJsonLd } from './components/JsonLd';
import { supabase } from './lib/supabase';

// Custom Link component that scrolls to top with logging
const ScrollToTopLink = ({ children, onClick, ...props }) => {
  const handleClick = (e) => {
    console.log('ScrollToTop: Starting scroll');
    try {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
        ...(CSS.supports('scroll-behavior: smooth') ? {} : { behavior: 'auto' })
      });
      console.log('ScrollToTop: Scroll completed');
      if (onClick) onClick(e);
    } catch (error) {
      console.error('ScrollToTop Error:', error);
      window.scrollTo(0, 0);
      if (onClick) onClick(e);
    }
  };

  return (
    <Link {...props} onClick={handleClick}>
      {children}
    </Link>
  );
};

function Logo({ className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="relative">
        <div className="relative bg-gradient-to-br from-gold-500 via-gold-400 to-gold-600 p-2.5 rounded-full shadow-lg shadow-gold-200/50 coin-glow">
          <CircleDollarSign className="h-7 w-7 text-white" />
        </div>
        <div className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 bg-primary-500 rounded-full border-2 border-white animate-pulse" />
      </div>
      <div className="flex flex-col">
        <span className="text-2xl font-display font-bold text-gradient-gold">
          OldCoinage
        </span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 -mt-1 font-medium">
          Premium Collectibles
        </span>
      </div>
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Close menus on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        setIsAdmin(profile?.is_admin || false);
      } catch {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  const handleSignOut = async () => {
    try {
      localStorage.removeItem('oldcoinage_auth');
      localStorage.removeItem('supabase.auth.token');
      
      await supabase.auth.signOut();
      
      setUser(null);
      setIsAdmin(false);
      setIsMobileMenuOpen(false);
      setIsDropdownOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
      setUser(null);
      setIsAdmin(false);
      navigate('/');
    }
  };

  const handleMobileMenuClick = () => {
    setIsMobileMenuOpen(false);
  };

  const handleDropdownClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDropdownLinkClick = () => {
    setIsDropdownOpen(false);
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>OldCoinage.com - Rare Coin Collection Marketplace</title>
          <meta name="description" content="Discover and collect rare coins and currency notes. OldCoinage.com is your premier marketplace for numismatic treasures." />
          <meta name="keywords" content="coins, rare coins, currency notes, numismatics, coin collecting, antique coins" />
        </Helmet>
        <OrganizationJsonLd />
        <WebSiteJsonLd />

        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-soft transition-all duration-300">
          <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 text-white py-2 relative overflow-hidden">
            <div className="absolute inset-0 bg-coin-texture opacity-30"></div>
            <div className="max-w-7xl mx-auto px-4 relative">
              <p className="text-sm text-center flex items-center justify-center gap-2">
                <Shield className="h-4 w-4" />
                Trusted by 10,000+ collectors worldwide
              </p>
            </div>
          </div>

          <div className="border-b border-gray-100/50">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between h-20">
                <ScrollToTopLink to="/" className="group shrink-0" onClick={handleMobileMenuClick}>
                  <Logo />
                </ScrollToTopLink>

                <div className="hidden lg:flex items-center gap-4">
                  <Link
                    to="/sell"
                    className="premium-button-gold"
                  >
                    Sell Your Coins
                  </Link>
                  <div className="flex items-center gap-4">
                    {user ? (
                      <>
                        {isAdmin && (
                          <>
                            <ScrollToTopLink
                              to="/upload"
                              className="premium-button-primary"
                            >
                              Add New Listing
                            </ScrollToTopLink>
                            <ScrollToTopLink
                              to="/bulk-upload"
                              className="premium-button-outline"
                            >
                              Bulk Upload
                            </ScrollToTopLink>
                          </>
                        )}
                        <div className="relative">
                          <button 
                            onClick={handleDropdownClick}
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-primary-600 font-medium"
                          >
                            <User className="h-5 w-5" />
                            {isAdmin ? 'Admin' : 'Account'}
                          </button>
                          {isDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1">
                              {isAdmin && (
                                <>
                                  <ScrollToTopLink
                                    to="/admin/orders"
                                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    onClick={handleDropdownLinkClick}
                                  >
                                    <Package className="h-4 w-4" />
                                    View Orders
                                  </ScrollToTopLink>
                                  <ScrollToTopLink
                                    to="/admin/collectibles"
                                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    onClick={handleDropdownLinkClick}
                                  >
                                    <CircleDollarSign className="h-4 w-4" />
                                    Manage Collectibles
                                  </ScrollToTopLink>
                                  <ScrollToTopLink
                                    to="/admin/users"
                                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    onClick={handleDropdownLinkClick}
                                  >
                                    <Users className="h-4 w-4" />
                                    Manage Users
                                  </ScrollToTopLink>
                                  <ScrollToTopLink
                                    to="/admin/submissions"
                                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    onClick={handleDropdownLinkClick}
                                  >
                                    <Upload className="h-4 w-4" />
                                    Seller Submissions
                                  </ScrollToTopLink>
                                  <ScrollToTopLink
                                    to="/admin/blog"
                                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    onClick={handleDropdownLinkClick}
                                  >
                                    <BookOpen className="h-4 w-4" />
                                    Manage Blog
                                  </ScrollToTopLink>
                                  <ScrollToTopLink
                                    to="/admin/promo-codes"
                                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    onClick={handleDropdownLinkClick}
                                  >
                                    <Tag className="h-4 w-4" />
                                    Promo Codes
                                  </ScrollToTopLink>
                                  <ScrollToTopLink
                                    to="/admin/categories"
                                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    onClick={handleDropdownLinkClick}
                                  >
                                    <FolderOpen className="h-4 w-4" />
                                    Categories
                                  </ScrollToTopLink>
                                </>
                              )}
                              <button
                                onClick={() => {
                                  handleSignOut();
                                  handleDropdownLinkClick();
                                }}
                                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <LogOut className="h-4 w-4" />
                                Sign Out
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <button
                        onClick={() => setShowAuthModal(true)}
                        className="flex items-center gap-2 px-6 py-2 bg-white border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 transition-all shadow-sm font-medium"
                      >
                        <LogIn className="h-5 w-5" />
                        Sign In
                      </button>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2 text-gray-600 hover:text-primary-600"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-200 py-4 px-4 bg-white">
              <nav className="space-y-4">
                {isAdmin && (
                  <>
                    <ScrollToTopLink
                      to="/admin/orders"
                      className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium"
                      onClick={handleMobileMenuClick}
                    >
                      <Package className="h-5 w-5" />
                      View Orders
                    </ScrollToTopLink>
                    <ScrollToTopLink
                      to="/admin/collectibles"
                      className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium"
                      onClick={handleMobileMenuClick}
                    >
                      <CircleDollarSign className="h-5 w-5" />
                      Manage Collectibles
                    </ScrollToTopLink>
                    <ScrollToTopLink
                      to="/admin/users"
                      className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium"
                      onClick={handleMobileMenuClick}
                    >
                      <Users className="h-5 w-5" />
                      Manage Users
                    </ScrollToTopLink>
                    <ScrollToTopLink
                      to="/admin/submissions"
                      className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium"
                      onClick={handleMobileMenuClick}
                    >
                      <Upload className="h-5 w-5" />
                      Seller Submissions
                    </ScrollToTopLink>
                    <ScrollToTopLink
                      to="/admin/blog"
                      className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium"
                      onClick={handleMobileMenuClick}
                    >
                      <BookOpen className="h-5 w-5" />
                      Manage Blog
                    </ScrollToTopLink>
                    <ScrollToTopLink
                      to="/admin/promo-codes"
                      className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium"
                      onClick={handleMobileMenuClick}
                    >
                      <Tag className="h-5 w-5" />
                      Promo Codes
                    </ScrollToTopLink>
                    <ScrollToTopLink
                      to="/admin/categories"
                      className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium"
                      onClick={handleMobileMenuClick}
                    >
                      <FolderOpen className="h-5 w-5" />
                      Categories
                    </ScrollToTopLink>
                  </>
                )}
                {user ? (
                  <>
                    {isAdmin && (
                      <>
                        <ScrollToTopLink
                          to="/upload"
                          className="block px-6 py-2 bg-primary-600 text-white rounded-full text-center font-medium"
                          onClick={handleMobileMenuClick}
                        >
                          Add New Listing
                        </ScrollToTopLink>
                        <ScrollToTopLink
                          to="/bulk-upload"
                          className="block px-6 py-2 bg-primary-600 text-white rounded-full text-center font-medium"
                          onClick={handleMobileMenuClick}
                        >
                          Bulk Upload
                        </ScrollToTopLink>
                      </>
                    )}
                    <button
                      onClick={() => {
                        handleSignOut();
                        handleMobileMenuClick();
                      }}
                      className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium w-full"
                    >
                      <LogOut className="h-5 w-5" />
                      Sign Out
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setShowAuthModal(true);
                      handleMobileMenuClick();
                    }}
                    className="flex items-center gap-2 text-gray-600 hover:text-primary-600 font-medium w-full"
                  >
                    <LogIn className="h-5 w-5" />
                    Sign In
                  </button>
                )}
              </nav>
            </div>
          )}
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          <Routes>
            <Route path="/" element={<CollectiblesList />} />
            <Route path="/upload" element={<UploadCollectible />} />
            <Route path="/bulk-upload" element={<BulkUpload />} />
            <Route path="/sell" element={<SellCollectible />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/return-policy" element={<ReturnPolicy />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/collectibles" element={<AdminCollectibles />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/submissions" element={<AdminSellerSubmissions />} />
            <Route path="/admin/blog" element={<AdminBlogPosts />} />
            <Route path="/admin/blog/new" element={<BlogPostEditor />} />
            <Route path="/admin/blog/edit/:id" element={<BlogPostEditor />} />
            <Route path="/admin/promo-codes" element={<AdminPromoCode />} />
            <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/edit/:id" element={<EditCollectible />} />
            <Route path="/:slug" element={<CollectibleDetail />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        <footer className="bg-white border-t mt-16">
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <ScrollToTopLink to="/" onClick={handleMobileMenuClick}>
                  <Logo className="mb-4" />
                </ScrollToTopLink>
                <p className="text-gray-600">
                  Your trusted marketplace for rare coins and currency notes.
                </p>
                <p className="mt-4 text-sm text-gray-500">
                  © {new Date().getFullYear()} OldCoinage.com<br />
                  All rights reserved.
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Resources</h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <ScrollToTopLink 
                      to="/blog" 
                      className="text-gray-600 hover:text-primary-600 flex items-center gap-2"
                      onClick={handleMobileMenuClick}
                    >
                      <BookOpen className="h-4 w-4" />
                      Numismatic Blog
                    </ScrollToTopLink>
                  </li>
                  <li>
                    <ScrollToTopLink 
                      to="/sell" 
                      className="text-gray-600 hover:text-primary-600 flex items-center gap-2"
                      onClick={handleMobileMenuClick}
                    >
                      <Upload className="h-4 w-4" />
                      Sell Your Coins
                    </ScrollToTopLink>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Legal</h3>
                <ul className="mt-4 space-y-4">
                  <li>
                    <ScrollToTopLink 
                      to="/privacy-policy" 
                      className="text-gray-600 hover:text-primary-600"
                      onClick={handleMobileMenuClick}
                    >
                      Privacy Policy
                    </ScrollToTopLink>
                  </li>
                  <li>
                    <ScrollToTopLink 
                      to="/terms-conditions" 
                      className="text-gray-600 hover:text-primary-600"
                      onClick={handleMobileMenuClick}
                    >
                      Terms & Conditions
                    </ScrollToTopLink>
                  </li>
                  <li>
                    <ScrollToTopLink 
                      to="/return-policy" 
                      className="text-gray-600 hover:text-primary-600"
                      onClick={handleMobileMenuClick}
                    >
                      Return Policy
                    </ScrollToTopLink>
                  </li>
                  <li>
                    <ScrollToTopLink 
                      to="/shipping-policy" 
                      className="text-gray-600 hover:text-primary-600"
                      onClick={handleMobileMenuClick}
                    >
                      Shipping Policy
                    </ScrollToTopLink>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Contact & Support</h3>
                <div className="mt-4 space-y-4">
                  <a
                    href={getWhatsAppLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors font-medium"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Chat on WhatsApp
                  </a>
                  <div className="text-gray-600">
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {CONTACT_INFO.email}
                    </p>
                    <p className="flex items-center gap-2 mt-2">
                      <Phone className="h-4 w-4" />
                      {CONTACT_INFO.phone}
                    </p>
                    <p className="text-sm text-gray-500 mt-4">
                      Business Hours:<br />
                      {CONTACT_INFO.businessHours}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;